import { CSSProperties, useEffect, useMemo } from 'react';
import { Box, styled, useTheme } from '@material-ui/core';
import { SText, SSearchInput, SHighlightText } from '@setvi/shared/components';
import { LocationPointIcon } from '@setvi/shared/components/sicons';
import { Customer } from 'Services/Mongo/Types';
import { distances } from '@setvi/shared/styles';
import { TextColor } from '@setvi/shared/enums';
import SLabel from '@setvi/shared/components/sui/slabel';
import {
  BaseDropdown,
  BaseDropdownProps,
  Option
} from 'pages/sales/modules/orders/components/filters/components/base-dropdown';
import { STextProps } from '@setvi/shared/components/sui/stext';
import { useCustomers } from './useCustomers';
import { normalizeCityName } from './utils';

export const FilterSearchInput = styled(SSearchInput)({
  width: '100%',
  borderRadius: 99,
  margin: `${distances.px.small} 0 ${distances.px.medium} 0`,
  background: '#F8F9FA',
  padding: `${distances.px.small} ${distances.px.base}`,
  '& fieldset': {
    borderColor: '#EEF0F2 !important'
  }
});

interface CustomerDropdownProps extends Pick<
  BaseDropdownProps,
  'placeholder' | 'disablePortal' | 'placement'
> {
  error?: string;
  label?: string;
  required?: boolean;
  newDesign?: boolean;
  selectedCustomer: Customer | null;
  style?: {
    input?: CSSProperties;
    dropdownWrapperWidth?: number;
    dropdownMaxHeight?: number;
  };
  onSelectCustomer: (customer: Customer) => void;
  selectButtonProps?: {
    fontColor: string;
    fontWeight: STextProps['weight'];
    fontSize: string;
  };
}

export const CustomerDropdown = ({
  newDesign = false,
  required,
  selectButtonProps,
  selectedCustomer,
  placement = 'bottom-end',
  disablePortal,
  label = '',
  error,
  style,
  onSelectCustomer,
  placeholder = 'Select Customer'
}: CustomerDropdownProps) => {
  const { palette } = useTheme();
  const {
    customers,
    isLoading,
    searchQuery,
    searchCustomers,
    fetchMoreSearchResults
  } = useCustomers();

  const options: Option[] = useMemo(
    () =>
      customers?.map((customer: Customer) => {
        const isSelected = customer?._id === selectedCustomer?._id;
        return {
          value: customer._id,
          label: customer.name,
          customItem: (
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              gridGap="4px">
              <SHighlightText
                value={customer.name}
                searchQuery={searchQuery}
                fontColor={isSelected ? TextColor.Blue : '#20262D'}
                lineHeight="24px"
                weight="bold"
              />
              {customer.accountNumber && (
                <SText
                  size="xs"
                  weight="bold"
                  lineHeight="14px"
                  fontColor={TextColor.Blue}
                  title={customer.accountNumber}
                />
              )}
              {customer.shippingState && customer.shippingCity && (
                <Box display="flex" alignItems="center" gridGap="2px">
                  <LocationPointIcon
                    style={{ width: 16, height: 16 }}
                    fill={isSelected && TextColor.Blue}
                  />
                  <SText
                    size="sm"
                    lineHeight="20px"
                    fontColor={
                      isSelected ? TextColor.Blue : TextColor.SlateGrey
                    }
                    title={`${customer.shippingCity ? `${normalizeCityName(customer.shippingCity)},` : ''} ${customer.shippingState || ''}`}
                  />
                </Box>
              )}
            </Box>
          )
        };
      }) || [],
    [customers, searchQuery, selectedCustomer?._id]
  );

  const selected = useMemo(
    () =>
      selectedCustomer
        ? { value: selectedCustomer._id, label: selectedCustomer.name }
        : null,
    [selectedCustomer]
  );

  useEffect(() => {
    // Fetch customers on component mount
    searchCustomers('');
  }, [searchCustomers]);

  return (
    <Box>
      {label && (
        <SLabel
          style={{ marginBottom: '5px', fontSize: '0.875rem' }}
          title={label}
          required={required}
        />
      )}
      <BaseDropdown
        anchorProps={{ newDesign, noBorder: true, ...selectButtonProps }}
        options={options}
        disablePortal={disablePortal}
        onSelect={option =>
          onSelectCustomer(
            option ? customers.find(i => i._id === option.value) : null
          )
        }
        isSelected={option => option?.value === selected?.value}
        title="Select Customer"
        placeholder={placeholder}
        searchPlaceholder="Search Customer..."
        selected={selected}
        styles={{ input: style?.input }}
        dimensions={{
          inputWidth: 250,
          dropdownWidth: style?.dropdownWrapperWidth || 400,
          maxHeight: style?.dropdownMaxHeight || 350
        }}
        placement={placement}
        isLoading={isLoading}
        searchValue={searchQuery}
        onSearchChange={searchCustomers}
        onFetchMore={fetchMoreSearchResults}
        searchable
        clearable
        emptyMessage="No customers found."
      />
      {error && (
        <SText
          style={{ marginTop: '7px', fontWeight: 500 }}
          fontColor={palette.error.main}
          size="sm"
          title={error}
        />
      )}
    </Box>
  );
};
